import { useState } from 'react';
import { observer } from 'mobx-react';
import {
  Table,
  Button,
  Space,
  Typography,
  Tooltip,
  Modal,
  message,
  type TableProps,
} from 'antd';
import {
  FilePdfOutlined,
  FileMarkdownOutlined,
  LinkOutlined,
  DeleteOutlined,
  ExportOutlined,
  CopyOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  CloudServerOutlined,
  EyeOutlined,
  InboxOutlined,
  ReloadOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { SourcePreviewDrawer, type PreviewSource } from './SourcePreviewDrawer';
import { EditSourceDrawer } from './EditSourceDrawer';
import type { ColumnsType } from 'antd/es/table';
import { useStore } from '@/stores';
import type { SourceKind, UnifiedSource } from '@/api/types';
import { reindexSource } from '@/api/knowledge';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import { StatusPill } from '@/components/StatusPill';
import { BrandedEmpty } from '@/components/BrandedEmpty';

const { Text } = Typography;

// One table for PDFs + Markdown + Links. Each row carries a `kind` so we know
// what to render for the title icon, the secondary action (preview vs. open
// link), and which delete endpoint to hit.
//
// Indexed-state pill (Azure AI Search): if `documentId` is non-null, the
// source has been chunked, embedded, and is available for RAG retrieval.
// We surface this so the user can immediately tell whether a freshly-uploaded
// file is "live" or still being processed.

const KIND_CONFIG: Record<
  SourceKind,
  {
    icon: React.ComponentType;
    label: string;
    iconBg: string;
    iconBgDark: string;
    iconColor: string;
  }
> = {
  pdf: {
    icon: FilePdfOutlined,
    label: 'PDF',
    iconBg: colors.ink[50],
    iconBgDark: `${colors.ink[700]}55`,
    iconColor: colors.ink[600],
  },
  markdown: {
    icon: FileMarkdownOutlined,
    label: 'Markdown',
    iconBg: colors.violet[50],
    iconBgDark: `${colors.violet[700]}55`,
    iconColor: colors.violet[600],
  },
  link: {
    icon: LinkOutlined,
    label: 'Link',
    iconBg: colors.amber[100],
    iconBgDark: `${colors.amber[700]}55`,
    iconColor: colors.amber[600],
  },
};

interface SourceTableProps {
  showHeader?: boolean;
}

export const SourceTable = observer(function SourceTable({ showHeader = true }: SourceTableProps) {
  const { knowledgeDetailStore: kb, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);

  // Preview drawer state — opens when the user clicks the title or a
  // dedicated "Preview" action on a PDF / Markdown row. Lifted into
  // the table component so the iframe / markdown render isn't torn
  // down between row selections.
  const [previewSource, setPreviewSource] = useState<PreviewSource | null>(null);
  // Edit-source drawer state — separate from preview so the user can have a
  // preview open and edit a different row without one closing the other.
  const [editingSource, setEditingSource] = useState<UnifiedSource | null>(null);

  const columns: ColumnsType<UnifiedSource> = [
    {
      title: 'Source',
      key: 'title',
      sorter: (a, b) => a.title.localeCompare(b.title),
      render: (_text, row) => {
        const cfg = KIND_CONFIG[row.kind];
        const Icon = cfg.icon;
        return (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 7,
                background: isDark ? cfg.iconBgDark : cfg.iconBg,
                color: cfg.iconColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 15,
                flexShrink: 0,
              }}
            >
              <Icon />
            </div>
            <div style={{ minWidth: 0 }}>
              {(row.kind === 'pdf' || row.kind === 'markdown') && row.blobUrl ? (
                // Title doubles as the primary "open preview" affordance —
                // click anywhere on the file name to read it in the side
                // drawer. Underline-on-hover signals that it's interactive.
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewSource({
                      kind: row.kind,
                      title: row.title,
                      blobUrl: row.blobUrl,
                    });
                  }}
                  style={{
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: isDark ? colors.slate[50] : colors.slate[900],
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 420,
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'block',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  {row.title}
                </button>
              ) : (
                <div
                  style={{
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: isDark ? colors.slate[50] : colors.slate[900],
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 420,
                  }}
                >
                  {row.title}
                </div>
              )}
              {row.url ? (
                <div
                  style={{
                    fontSize: 11.5,
                    fontFamily: "'JetBrains Mono', monospace",
                    color: isDark ? colors.slate[400] : colors.slate[500],
                    marginTop: 2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 420,
                  }}
                >
                  {row.url}
                </div>
              ) : row.description ? (
                <div
                  style={{
                    fontSize: 12,
                    color: isDark ? colors.slate[400] : colors.slate[500],
                    marginTop: 2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 420,
                  }}
                >
                  {row.description}
                </div>
              ) : null}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Type',
      key: 'kind',
      width: 110,
      filters: [
        { text: 'PDF', value: 'pdf' },
        { text: 'Markdown', value: 'markdown' },
        { text: 'Link', value: 'link' },
      ],
      onFilter: (val, row) => row.kind === val,
      render: (_text, row) => (
        <StatusPill
          tone={row.kind === 'pdf' ? 'ink' : row.kind === 'markdown' ? 'violet' : 'amber'}
        >
          {KIND_CONFIG[row.kind].label}
        </StatusPill>
      ),
    },
    {
      title: (
        <Tooltip title="When checked, the source has been chunked, embedded, and is queryable by Azure AI Search at retrieval time.">
          <span>
            <CloudServerOutlined style={{ marginRight: 5 }} />
            Indexed
          </span>
        </Tooltip>
      ),
      key: 'indexed',
      width: 130,
      filters: [
        { text: 'Indexed', value: 'yes' },
        { text: 'Pending', value: 'no' },
      ],
      onFilter: (val, row) => (val === 'yes' ? !!row.documentId : !row.documentId),
      render: (_text, row) =>
        row.documentId ? (
          <Tooltip title={`docId: ${row.documentId}`}>
            <span>
              <StatusPill tone="green" icon={<CheckCircleFilled />}>
                Indexed
              </StatusPill>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title="Source uploaded but not yet chunked + embedded. Indexing is async — usually under a minute. Refresh to check.">
            <span>
              <StatusPill tone="amber" icon={<ClockCircleOutlined />}>
                Pending
              </StatusPill>
            </span>
          </Tooltip>
        ),
    },
    {
      // Tags column. Drives:
      //   - Visual signal on the row ("which sources are NCCN-2024?")
      //   - Header filter (click the funnel) so the user can scope the
      //     Sources table to a single tag without losing the rest of
      //     the table state.
      // Tags themselves are authored from the Edit-source drawer; this
      // column is read-only display + filter.
      title: 'Tags',
      key: 'tags',
      width: 220,
      filterMode: 'menu' as const,
      filterSearch: true,
      filters: (() => {
        // Build the available-tags list dynamically from the rows currently
        // in the table. Sorted alphabetically so the filter menu is stable
        // across re-renders.
        const all = new Set<string>();
        for (const r of kb.sources) for (const t of r.tags ?? []) all.add(t);
        return Array.from(all).sort().map((t) => ({ text: t, value: t }));
      })(),
      onFilter: (val: React.Key | boolean, row: UnifiedSource) =>
        (row.tags ?? []).includes(String(val)),
      render: (_text, row) => {
        const tags = row.tags ?? [];
        if (tags.length === 0) {
          return <Text type="secondary" style={{ fontSize: 11, opacity: 0.7 }}>—</Text>;
        }
        // Cap at 3 visible; rest in a tooltip pill so wide tag-stacks don't
        // explode the row height.
        const visible = tags.slice(0, 3);
        const overflow = tags.length - visible.length;
        return (
          <Space size={4} wrap>
            {visible.map((t) => (
              <StatusPill key={t} tone="violet">{t}</StatusPill>
            ))}
            {overflow > 0 && (
              <Tooltip title={tags.slice(3).join(', ')}>
                <span>
                  <StatusPill tone="slate">+{overflow}</StatusPill>
                </span>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Added',
      dataIndex: 'createdDate',
      key: 'createdDate',
      width: 140,
      sorter: (a, b) => (a.createdDate ?? '').localeCompare(b.createdDate ?? ''),
      defaultSortOrder: 'descend' as const,
      render: (v?: string) => {
        if (!v) return <Text type="secondary">—</Text>;
        try {
          const d = new Date(v);
          return (
            <Text style={{ fontSize: 12, color: isDark ? colors.slate[400] : colors.slate[500] }}>
              {d.toLocaleDateString()}{' '}
              <span style={{ opacity: 0.7 }}>
                {d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </Text>
          );
        } catch {
          return <Text type="secondary">{v}</Text>;
        }
      },
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      align: 'right',
      fixed: 'right',
      onCell: () => ({ onClick: (e) => e.stopPropagation() }),
      render: (_text, row) => (
        <Space size={2}>
          {row.kind === 'link' && row.url && (
            <Tooltip title="Open in new tab">
              <Button
                type="text"
                size="small"
                icon={<ExportOutlined />}
                onClick={() => window.open(row.url, '_blank', 'noopener')}
              />
            </Tooltip>
          )}
          {(row.kind === 'pdf' || row.kind === 'markdown') && row.blobUrl && (
            <>
              <Tooltip title="Preview in a side drawer">
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() =>
                    setPreviewSource({
                      kind: row.kind,
                      title: row.title,
                      blobUrl: row.blobUrl,
                    })
                  }
                />
              </Tooltip>
              <Tooltip title="Open in a new tab">
                <Button
                  type="text"
                  size="small"
                  icon={<ExportOutlined />}
                  onClick={() => window.open(row.blobUrl, '_blank', 'noopener')}
                />
              </Tooltip>
            </>
          )}
          {row.documentId && (
            <Tooltip title={`Copy doc id\n${row.documentId}`}>
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => {
                  void navigator.clipboard?.writeText(row.documentId ?? '');
                  message.success('Document id copied');
                }}
              />
            </Tooltip>
          )}
          {/*
            Reindex action — only surfaces when documentId is null, indicating
            the source is in Pending state (initial indexing failed, typically
            a transient Azure AI Search 503). The row otherwise looks normal
            (DB row, blob saved, Sources count includes it) but RAG retrieval
            won't return content from it. Clicking this re-runs the indexing
            pipeline server-side, which now retries transient errors with
            exponential backoff (AzureSearchRetry on the backend).
          */}
          {!row.documentId && row.kind !== 'link' && (
            <Tooltip title="This source isn't indexed yet — try indexing it again">
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined />}
                onClick={async () => {
                  try {
                    await reindexSource(row.kind, row.id);
                    message.success(`Re-indexed "${row.title}"`);
                    await kb.refresh();
                    trackEvent('Knowledge.SourceReindexed', {
                      kind: row.kind,
                      sourceId: row.id,
                    });
                  } catch (err) {
                    message.error(err instanceof Error ? err.message : 'Reindex failed');
                  }
                }}
              >
                Reindex
              </Button>
            </Tooltip>
          )}
          {!row.documentId && row.kind === 'link' && (
            <Tooltip title="This link isn't indexed yet — try fetching the page again">
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined />}
                onClick={async () => {
                  try {
                    await reindexSource('link', row.id);
                    message.success(`Re-indexed "${row.title}"`);
                    await kb.refresh();
                    trackEvent('Knowledge.SourceReindexed', { kind: 'link', sourceId: row.id });
                  } catch (err) {
                    message.error(err instanceof Error ? err.message : 'Reindex failed');
                  }
                }}
              >
                Reindex
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Edit description, tags, and other metadata">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingSource(row);
                trackEvent('Knowledge.SourceEditOpened', {
                  kind: row.kind,
                  sourceId: row.id,
                });
              }}
            />
          </Tooltip>
          <Tooltip title="Delete source">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                Modal.confirm({
                  title: `Delete this ${KIND_CONFIG[row.kind].label.toLowerCase()}?`,
                  content: `"${row.title}" will be removed and its index entry deleted.`,
                  okText: 'Delete',
                  okButtonProps: { danger: true },
                  onOk: async () => {
                    try {
                      await kb.deleteSource(row.kind, row.id);
                      message.success('Source deleted');
                      trackEvent('Knowledge.SourceDeleted', { kind: row.kind, id: row.id });
                    } catch (err) {
                      message.error(err instanceof Error ? err.message : 'Delete failed');
                    }
                  },
                });
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const rowSelection: TableProps<UnifiedSource>['rowSelection'] = {
    selectedRowKeys: selectedKeys,
    onChange: (keys) => setSelectedKeys(keys),
  };

  function handleBulkDelete(): void {
    const items = kb.sources
      .filter((s) => selectedKeys.includes(`${s.kind}:${s.id}`))
      .map((s) => ({ kind: s.kind, id: s.id }));
    if (items.length === 0) return;
    Modal.confirm({
      title: `Delete ${items.length} ${items.length === 1 ? 'source' : 'sources'}?`,
      content: 'They will be removed and their index entries deleted.',
      okText: `Delete ${items.length}`,
      okButtonProps: { danger: true },
      onOk: async () => {
        await kb.deleteMany(items);
        setSelectedKeys([]);
        message.success(`Deleted ${items.length} sources`);
      },
    });
  }

  // Use composite keys because pdf/markdown/link ids overlap.
  // Composite key because pdf/markdown/link ids overlap.
  const rowKey = (s: UnifiedSource) => `${s.kind}:${s.id}`;

  return (
    <div>
      {showHeader && selectedKeys.length > 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 14px',
            background: isDark ? colors.slate[700] : colors.ink[50],
            border: `1px solid ${isDark ? colors.slate[600] : colors.ink[200]}`,
            borderRadius: 8,
            marginBottom: 10,
          }}
        >
          <Text style={{ fontSize: 13, color: isDark ? colors.ink[100] : colors.ink[700] }}>
            <b>{selectedKeys.length}</b> selected
          </Text>
          <Space>
            <Button size="small" onClick={() => setSelectedKeys([])}>
              Clear
            </Button>
            <Button danger size="small" icon={<DeleteOutlined />} onClick={handleBulkDelete}>
              Delete selected
            </Button>
          </Space>
        </div>
      )}
      <Table<UnifiedSource>
        rowKey={rowKey}
        columns={columns}
        dataSource={kb.sources}
        loading={kb.isLoading}
        rowSelection={rowSelection}
        pagination={{
          pageSize: 25,
          showSizeChanger: true,
          showTotal: (t) => `${t} ${t === 1 ? 'source' : 'sources'}`,
          style: { padding: '12px 0', margin: 0 },
        }}
        scroll={{ x: 'max-content' }}
        locale={{
          emptyText: (
            // InboxOutlined is the universally-recognized "empty container"
            // icon — no orbiting dots that a non-tech user could misread as
            // a loading spinner. (Reported issue: clinical SMEs interpreted
            // the previous file-with-dots glyph as "the system is still
            // working on something" and waited indefinitely.)
            <BrandedEmpty
              icon={<InboxOutlined />}
              title="No sources yet"
              description="Drop PDFs, Markdown, or text files into the upload area above — or use the “Add link” button to add a URL."
              size="md"
            />
          ),
        }}
      />

      <SourcePreviewDrawer
        source={previewSource}
        onClose={() => setPreviewSource(null)}
      />

      <EditSourceDrawer
        open={editingSource != null}
        source={editingSource}
        onClose={() => setEditingSource(null)}
        onSaved={() => {
          // Refresh the unified source list — backend re-indexed in the same
          // request, so the user's next Test Query reflects the new tags.
          void kb.refresh();
        }}
      />
    </div>
  );
});
